import { el } from '../utils/dom.js';
import { auth } from '../store/auth.js';
import { Auth } from '../api/resources.js';
import { navigate } from '../router/index.js';
import { buildNotificationBell, stopPolling } from './notification-bell.js';
import { mountAppSwitcher } from '../studio-app-switcher.js';

const NAV = [
  { section: 'Operacion' },
  { path: '/',             label: 'Dashboard',     icon: '📊' },
  { path: '/inventory',    label: 'Inventario',    icon: '📦', asistente: true },
  { path: '/loans',        label: 'Prestamos',     icon: '🤝' },
  { path: '/rentals',      label: 'Rentas',        icon: '💰', rental: true },
  { path: '/calendario',   label: 'Calendario',    icon: '🗓️' },
  { path: '/mis-entregas', label: 'Mis entregas',  icon: '🚚', rental: true, asistente: true },
  { path: '/reservations', label: 'Reservas',      icon: '📅' },
  { path: '/maintenance',  label: 'Mantenimiento', icon: '🔧', asistente: true },
  { section: 'Catalogo' },
  { path: '/categories',   label: 'Categorias',    icon: '🏷️' },
  { path: '/locations',    label: 'Ubicaciones',   icon: '📍' },
  { path: '/clients',      label: 'Clientes',      icon: '👥' },
  { path: '/responsibles', label: 'Responsables',  icon: '👤' },
  { section: 'Sistema' },
  { path: '/reports',      label: 'Reportes',      icon: '📈' },
  { path: '/users',        label: 'Usuarios',      icon: '🔑' },
  { path: '/settings',     label: 'Configuracion', icon: '⚙️' },
];

let currentPath = '/';

export function buildShell(outletEl) {
  const sidebar = buildSidebar();
  const topbar  = buildTopbar(sidebar);
  const main    = el('main', { class: 'main' }, outletEl);

  return el('div', { class: 'app-shell' }, sidebar, topbar, main);
}

function buildSidebar() {
  const user = auth.user || {};
  const initials = (user.full_name || user.email || '?')
    .split(' ').map((s) => s[0]).join('').slice(0, 2).toUpperCase();

  const navEl = el('div', { class: 'nav' });
  // El asistente (puesto "tecnico") solo ve lo suyo: sin precios ni administración.
  const esAsistente = (auth.user || {}).role === 'tecnico';
  for (const item of NAV) {
    if (esAsistente && (item.section || !item.asistente)) continue;
    if (item.section) {
      navEl.appendChild(el('div', { class: 'section' }, item.section));
    } else {
      const a = el('a', {
        href: '#' + item.path,
        dataset: { path: item.path, ...(item.rental ? { rental: '1' } : {}) },
        onclick: () => closeSidebarMobile(),
      },
        el('span', { class: 'nav-ico' }, item.icon || ''),
        el('span', {}, item.label),
      );
      navEl.appendChild(a);
    }
  }

  const userBlock = el('div', { class: 'user-block' },
    el('div', { class: 'avatar' }, initials),
    el('div', { class: 'flex-1 truncate' },
      el('div', { class: 'text-bold truncate' }, user.full_name || 'Usuario'),
      el('div', { class: 'text-xs muted truncate' }, user.email || ''),
    ),
    el('button', {
      class: 'btn ghost icon',
      title: 'Cerrar sesion',
      onclick: async () => {
        try { await Auth.logout(); } catch {}
        auth.clear();
        navigate('/login');
      },
    }, '⎋'),
  );

  // AppSwitcher mount point. Se mounta tras attach al DOM (ver hook below).
  const switcherMount = el('div', { class: 'brand-block', id: 'studio-switcher-mount', style: 'padding: 8px;' });

  const aside = el('aside', { class: 'sidebar', id: 'sidebar' },
    switcherMount,
    navEl,
    userBlock,
  );

  // En modo "solo controlar mis equipos" no se ven las rentas.
  import('../api/resources.js').then(({ Rentals }) => Rentals.config()).then((cfg) => {
    if (cfg?.modo === 'tradicional') navEl.querySelectorAll('[data-rental]').forEach((n) => { n.hidden = true; });
  }).catch(() => {});

  // Mount AppSwitcher en el siguiente tick (cuando el aside ya esté en DOM).
  // requestAnimationFrame asegura que el elemento exista cuando lo busquemos.
  requestAnimationFrame(() => {
    const mountNode = document.getElementById('studio-switcher-mount');
    if (mountNode) {
      mountAppSwitcher({ container: mountNode, currentSystem: 'inventory' });
    }
  });

  return aside;
}

function buildTopbar(sidebar) {
  // Cleanup paneles previos (evita duplicados al re-login)
  document.querySelectorAll('[data-notification-panel]').forEach((n) => n.remove());
  const { bell, panel } = buildNotificationBell();
  panel.dataset.notificationPanel = '1';
  document.body.appendChild(panel);
  return el('header', { class: 'topbar', style: 'position: relative;' },
    el('button', {
      class: 'btn ghost menu-toggle',
      onclick: () => sidebar.classList.toggle('open'),
    }, '☰'),
    el('h1', { id: 'page-title' }, 'Inventario'),
    el('div', { class: 'flex-1' }),
    bell,
    el('button', {
      class: 'btn ghost icon',
      title: 'Cambiar tema',
      onclick: toggleTheme,
    }, '🌓'),
  );
}

function closeSidebarMobile() {
  const s = document.getElementById('sidebar');
  if (s) s.classList.remove('open');
}

function toggleTheme() {
  const cur = document.documentElement.dataset.theme || (matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
  const next = cur === 'dark' ? 'light' : 'dark';
  document.documentElement.dataset.theme = next;
  localStorage.setItem('theme', next);
}

export function setActiveNav(path) {
  currentPath = path;
  document.querySelectorAll('.nav a').forEach((a) => {
    const p = a.dataset.path;
    a.classList.toggle('active', p === path || (p !== '/' && path.startsWith(p)));
  });
}

export function setPageTitle(title) {
  const t = document.getElementById('page-title');
  if (t) t.textContent = title;
  document.title = title + ' · Inventario';
}

export function applySavedTheme() {
  const saved = localStorage.getItem('theme');
  if (saved) document.documentElement.dataset.theme = saved;
  const tema = localStorage.getItem('tema');
  if (tema) document.documentElement.dataset.tema = tema;
  cargarTema();
}

// Tema de la empresa (ajuste `app.tema`): se guarda para pintarlo sin parpadeo la próxima vez.
export function ponerTema(tema) {
  const t = tema && tema !== 'clasico' ? tema : '';
  if (t) document.documentElement.dataset.tema = t; else delete document.documentElement.dataset.tema;
  try { if (t) localStorage.setItem('tema', t); else localStorage.removeItem('tema'); } catch { /* sin almacenamiento */ }
}

async function cargarTema() {
  try {
    const r = await fetch('/api/publico/tema', { cache: 'no-store' });
    if (r.ok) ponerTema((await r.json()).tema);
  } catch { /* sin conexión: se queda el guardado */ }
}
